import React, { useEffect, useMemo, useState } from 'react';
import { TableProps } from './Table.types';
import { twMerge } from '@shared/utils/twMerge';

const HIGHLIGHT_DURATION = 20000;

export const TableRows = <T,>({
  data,
  columns,
  loading,
  loadingLength = 10,
  styles,
  highlightDuration = HIGHLIGHT_DURATION,
  shouldHighlightRow,
  isRowDisabled,
  onHighlightFinish,
  onRowClick,
  darkMode,
}: Pick<
  TableProps<T>,
  | 'data'
  | 'columns'
  | 'loading'
  | 'styles'
  | 'loadingLength'
  | 'onHighlightFinish'
  | 'highlightDuration'
  | 'shouldHighlightRow'
  | 'isRowDisabled'
  | 'onRowClick'
  | 'darkMode'
>) => {
  const loadingRows = useMemo(() => [...Array(loadingLength).keys()], [loadingLength]);

  const [highlightedRowIndex, setHighlightedRowIndex] = useState<number | null>(null);

  const rows = useMemo(() => (loading ? loadingRows : data || []), [loading, loadingRows, data]);

  useEffect(() => {
    if (highlightedRowIndex === -1) {
      return;
    }
    const timeoutId = setTimeout(() => {
      onHighlightFinish?.();
      setHighlightedRowIndex(null);
    }, highlightDuration);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [highlightDuration, highlightedRowIndex, onHighlightFinish]);

  useEffect(() => {
    const highlightedIndex = rows.findIndex(row =>
      shouldHighlightRow ? shouldHighlightRow(row) : false
    );
    setHighlightedRowIndex(highlightedIndex);
  }, [rows, shouldHighlightRow]);

  return (
    <>
      {rows.map((row, rowIndex) => {
        return (
          <tr
            key={rowIndex}
            className={twMerge(
              'tr group/row p-0',
              isRowDisabled && isRowDisabled(row) && 'opacity-50',
              onRowClick && 'cursor-pointer',
              styles?.bodyTr
            )}
            {...(onRowClick && {
              onClick: () => onRowClick(row, rowIndex),
            })}
          >
            {columns.map((column, colIndex) => {
              const isFirstColumn = colIndex === 0;
              const isLastColumn = colIndex === columns.length - 1;

              return (
                <td
                  key={colIndex}
                  style={column.tdStyle}
                  className={twMerge(
                    'tr group/row p-0',
                    rowIndex === highlightedRowIndex && 'bg-waterLight',
                    styles?.td
                  )}
                >
                  <div
                    style={column.cellStyle}
                    className={twMerge(
                      'mini:text-1216 text-1624 flex h-full min-h-[5.7rem] items-center',
                      'border-b font-medium ',
                      darkMode
                        ? 'group-hover/row:bg-pageBackgroundDark border-pageBackgroundDark'
                        : 'group-hover/row:bg-waterLightHover50',
                      isFirstColumn ? 'pl-3 ' : 'px-1',
                      column.alignToRight ? 'justify-end' : 'justify-start',
                      isLastColumn ? ' pr-2' : '',
                      styles?.bodyCell,
                      column?.cellClassName
                    )}
                  >
                    {loading && column.loading
                      ? column.loading(rowIndex)
                      : column.getValue(row as T, rowIndex, colIndex)}
                  </div>
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
};
